import { useTranslation } from 'react-i18next';
import { EMPTY_VALUE, IJob, JobStatus } from '../../../common';
import ContentArea from './ContentArea';
import {
    Box,
    Card,
    Grid,
    Icon,
    IconButton,
    Typography,
    useAppDispatch,
    useAppSelector,
    useTheme,
} from '../../../base';
import { StyledIconButton } from './JobOverviewContent.styles.ts';
import HeaderLabels from '../HeaderLabels';
import { jobsActions, jobsSelectors } from '../../../actions';
import { useLocation, useNavigate } from 'react-router-dom';
import { RoutePaths } from '../../../routes/RoutePaths.ts';

const JobOverviewContent = ({
    isEmployerView = false,
    isPreview = false,
}: {
    isEmployerView?: boolean;
    isPreview?: boolean;
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const job: IJob =
        useAppSelector(jobsSelectors.getActiveJob) || ({} as IJob);
    const candidateSavedJobs = useAppSelector(
        jobsSelectors.getCandidateSavedJobs
    );
    const stepperInfo = useAppSelector(jobsSelectors.getJobStepperInfo);

    const contentAreaContent: Array<{ title: string; content: string }> = [
        {
            title: t('jobPage.companyOverview'),
            content: job.companyOverview || '',
        },
        { title: t('jobPage.jobOverview'), content: job.description || '' },
        {
            title: t('jobPage.responsibilities'),
            content: job.responsibilities || '',
        },
    ];

    const handleBookmarkClick = () => {
        let candidateSavedJobsIds = candidateSavedJobs;

        if (candidateSavedJobsIds.includes(String(job.id))) {
            candidateSavedJobsIds = candidateSavedJobsIds.filter(
                (id) => id !== String(job.id)
            );
        } else {
            candidateSavedJobsIds = [...candidateSavedJobsIds, String(job.id)];
        }

        dispatch(
            jobsActions.updateCandidateSavedJobs({
                jobIds: candidateSavedJobsIds,
            })
        );
    };

    const onEditClick = () => {
        dispatch(
            jobsActions.setStepperInfo({
                stepperInfo: {
                    ...stepperInfo,
                    activeStepIndex: 0,
                },
            })
        );
        if (!location.pathname.includes(RoutePaths.saveJob)) {
            navigate(`${location.pathname}/save-job${location.search}`);
        }
    };

    return (
        <Card
            sx={{
                width: '100%',
                paddingTop: theme.spacing(3),
                boxShadow: 'none',
                border: isPreview
                    ? `1px solid ${theme.palette.grey[400]}`
                    : 'none',
            }}
        >
            <Grid item sx={{ marginBottom: theme.spacing(1) }}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        marginTop: theme.spacing(0.5),
                    }}
                >
                    <Box display="flex" alignItems="center">
                        {!isEmployerView && !isPreview && (
                            <StyledIconButton
                                onClick={handleBookmarkClick} // Use the handler
                                borderColor={theme.palette.secondary.dark}
                            >
                                <Icon
                                    name={
                                        candidateSavedJobs.includes(
                                            String(job.id)
                                        )
                                            ? 'BookmarkFilledIcon'
                                            : 'BookmarkButtonIcon'
                                    }
                                    color={theme.palette.secondary.dark}
                                />
                            </StyledIconButton>
                        )}

                        <Typography
                            variant="body2"
                            sx={{
                                marginInlineEnd: theme.spacing(2),
                                color: theme.palette.grey[600],
                            }}
                        >
                            {`${t('positionPage.publishedOn')} ${
                                job.publishedDate || EMPTY_VALUE
                            }`}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.grey[600],
                            }}
                        >
                            {`${t('positionPage.jobId')} ${job.id}`}
                        </Typography>
                    </Box>
                    {(isEmployerView || isPreview) && job.status !== JobStatus.Legacy && (
                        <IconButton
                            onClick={onEditClick}
                            sx={{ marginInlineEnd: theme.spacing(-2) }}
                        >
                            <Icon name={'EditIcon'} />
                        </IconButton>
                    )}
                </Box>
            </Grid>
            <Grid item sx={{ marginBottom: theme.spacing(2) }}>
                <Box display="flex" alignItems="center">
                    <Typography variant="h4">{job.title}</Typography>
                </Box>
            </Grid>
            <Grid item sx={{ marginBottom: theme.spacing(2) }}>
                <HeaderLabels jobObject={job} />
            </Grid>
            {contentAreaContent.map((contentObj) => {
                return (
                    <ContentArea key={contentObj.title} content={contentObj} />
                );
            })}
        </Card>
    );
};

export default JobOverviewContent;
